import { Tooltip } from "@/base";
import { useFormControllerContext } from "@/contexts";
import {
    closestCenter,
    DndContext,
    type DragEndEvent,
    DragOverlay,
    type DragStartEvent,
    MouseSensor,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import {
    SortableContext,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
    Button,
    Card,
    CardBody,
    cn,
    type InputProps,
    Progress,
} from "@heroui/react";
import type { PressEvent } from "@react-types/shared";
import {
    IconBulb,
    IconCirclePlus,
    IconCopy,
    IconCopyCheck,
    IconInfoCircle,
} from "@tabler/icons-react";
import { type ReactNode, useCallback, useMemo, useState } from "react";
import {
    type FieldArray,
    type FieldArrayWithId,
    type FieldValues,
    type Path,
    type UseControllerProps,
    useFieldArray,
    type UseFieldArrayAppend,
    type UseFieldArrayMove,
    type UseFieldArrayRemove,
    type UseFieldArrayUpdate,
} from "react-hook-form";
import { type AIContext, MagicWand, type MagicWandProps } from "../magic-wand";
import {
    BaseTextArrayFieldItem,
    type ControlledTextArrayInputItemProps,
    type TextArrayInputItemProps,
} from "./text-array-field__item";
import { getValueAtPath } from "@sr-partner-portal/common";
export interface TextArrayValue {
    content: string;
}

type TextArrayFieldProps<
    T extends FieldValues,
    AIContextKeys extends string,
> = {
    label: string;
    emptyLabel?: string;
    showEmptyLabel?: boolean;
    hideItemLabel?: boolean;
    title?: string;
    fieldName: UseControllerProps<T>["name"];
    valuePropertyName: string;
    placeholder?: string;
    description?: ReactNode;
    hint?: ReactNode;
    isAutofocusing?: boolean;
    isDisabled?: boolean;
    type?: InputProps["type"];
    isRequired?: boolean;
    startContent?: InputProps["startContent"];
    endContent?: InputProps["endContent"];
    className?: string;
    cardBodyClassName?: string;
    rules?: {
        minLength?: number;
        maxLength?: number;
    };
    shouldRegister?: boolean;
    amountRecommendation?: {
        bad: number;
        okay: number;
        good: number;
    };
    hideAddButton?: boolean;
    disableDnd?: boolean;
    size?: "sm" | "md" | "lg";
    headerEndContent?: (append: (value?: string) => void) => ReactNode;
    cardEndContent?: (append: (value?: string) => void) => ReactNode;
    formController?: ReturnType<typeof useFormControllerContext>;
    isViewOnly?: boolean;
    canCopyAll?: boolean;
    hideItems?: boolean;
    staticItems?: string[];
    maxLength?: number;
    ai?: Pick<
        MagicWandProps<AIContext<AIContextKeys>, TextArrayValue[]>,
        | "context"
        | "onCreate"
        | "onUpdate"
        | "showPopover"
        | "additionalInformationEnabled"
        | "additionalInformationRequired"
        | "additionalInformationLabel"
        | "additionalInformationHint"
        | "rotateResultsAfter"
    > & { uniqueId: string | number };
    inputItem?: React.ComponentType<TextArrayInputItemProps>;
    controlledInputItem?: React.ComponentType<
        ControlledTextArrayInputItemProps<T>
    >;
};

type BaseTextArrayFieldProps<
    T extends FieldValues,
    AIContextKeys extends string = string,
> = TextArrayFieldProps<T, AIContextKeys> & {
    append?: UseFieldArrayAppend<FieldValues, Path<T>>;
    update?: UseFieldArrayUpdate<FieldValues, Path<T>>;
    move?: UseFieldArrayMove;
    remove?: UseFieldArrayRemove;
    fields?: FieldArrayWithId<FieldValues, Path<T>, "id">[];
    fieldError?: Record<string, { message: string }>[] | { message: string };
};

type RegisteredTextArrayFieldProps<
    T extends FieldValues,
    AIContextKeys extends string = string,
> = Omit<TextArrayFieldProps<T, AIContextKeys>, "shouldRegister">;

const BaseTextArrayField = <
    T extends FieldValues,
    AIContextKeys extends string = string,
>({
    description,
    hint,
    fieldName,
    valuePropertyName,
    isAutofocusing = false,
    isDisabled,
    label,
    emptyLabel = "Keine Einträge",
    showEmptyLabel = false,
    type,
    isRequired,
    startContent,
    endContent,
    title,
    className,
    cardBodyClassName,
    rules,
    append,
    move,
    remove,
    fields = [],
    formController,
    fieldError,
    amountRecommendation,
    hideAddButton,
    disableDnd,
    hideItemLabel,
    size,
    isViewOnly,
    canCopyAll = true,
    headerEndContent,
    cardEndContent,
    hideItems,
    staticItems,
    maxLength,
    inputItem,
    controlledInputItem,
}: BaseTextArrayFieldProps<T, AIContextKeys>) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = useCallback(async () => {
        try {
            const formattedField = fields
                .map((field) => `${field[valuePropertyName]}`)
                .join("\n");

            await navigator.clipboard.writeText(formattedField);

            setCopied(true);
            setTimeout(() => setCopied(false), 1000);
        } catch (err) {
            console.error("Failed to copy text: ", err);
        }
    }, [fields, valuePropertyName]);

    const handleAppend = (valueOrEvent: string | PressEvent = "") => {
        if (!append) {
            return;
        }

        const value = typeof valueOrEvent === "string" ? valueOrEvent : "";

        append({ [valuePropertyName]: value } as FieldArray<
            FieldValues,
            Path<T>
        >);
    };

    const disableAddButton = rules?.maxLength
        ? fields.length >= rules.maxLength
        : false;

    const fieldsLength = fields?.length || 0;

    const [activeItem, setActiveItem] = useState<
        FieldArray<FieldValues, Path<T>> & {
            id: string;
        }
    >();

    const sensors = useSensors(useSensor(MouseSensor));

    const fieldsWithIndexId = useMemo(
        () =>
            fields.map((field, index) => ({
                ...field,
                id: `${field.id}_${index}`,
            })),
        [fields],
    );

    const handleDragStart = (event: DragStartEvent) => {
        const { active } = event;
        const { id } = active;

        if (activeItem && activeItem.id === id) {
            return;
        }

        const newActiveItem = fieldsWithIndexId.find(
            (field) => field.id === id,
        );

        setActiveItem(newActiveItem);
    };

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (active.id !== over.id) {
            const activeIndex = Number.parseInt(
                active.id.toString().split("_")[1],
                10,
            );
            const overIndex = Number.parseInt(
                over.id.toString().split("_")[1],
                10,
            );
            move(activeIndex, overIndex);
        }
    };

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
        >
            <SortableContext
                items={fieldsWithIndexId}
                strategy={verticalListSortingStrategy}
                disabled={disableDnd || isViewOnly}
            >
                <div
                    className={cn(
                        "flex flex-col",
                        isDisabled && "opacity-disabled",
                        isViewOnly && "opacity-100",
                        className,
                    )}
                >
                    <Card
                        shadow="none"
                        classNames={{
                            base: "border border-default-200 flex-1",
                        }}
                    >
                        <CardBody
                            className={cn(
                                "flex flex-col gap-4 p-4",
                                size === "sm" && "gap-2",
                                size === "md" && "gap-3",
                                cardBodyClassName,
                            )}
                        >
                            {title && (
                                <div>
                                    <div className="mb-1 flex flex-row items-center gap-2 text-default-500 text-sm">
                                        <div className="flex w-full flex-row items-center gap-1">
                                            <div
                                                className={cn(
                                                    isRequired &&
                                                        "after:ms-0.5 after:text-danger after:content-['*']",
                                                )}
                                            >
                                                {title}
                                            </div>
                                            {description && (
                                                <Tooltip
                                                    color="primary"
                                                    content={description}
                                                >
                                                    <Button
                                                        variant="light"
                                                        isIconOnly={true}
                                                        size="sm"
                                                    >
                                                        <IconInfoCircle
                                                            stroke={1.5}
                                                            size={18}
                                                        />
                                                    </Button>
                                                </Tooltip>
                                            )}
                                            {hint && (
                                                <Tooltip
                                                    placement="right"
                                                    color="warning"
                                                    content={hint}
                                                >
                                                    <Button
                                                        variant="light"
                                                        isIconOnly={true}
                                                        size="sm"
                                                    >
                                                        <IconBulb
                                                            stroke={1.5}
                                                            size={18}
                                                        />
                                                    </Button>
                                                </Tooltip>
                                            )}
                                        </div>
                                        {!isViewOnly &&
                                            headerEndContent?.(handleAppend)}
                                    </div>
                                    {amountRecommendation && (
                                        <div className="flex items-center gap-2">
                                            <Progress
                                                classNames={{
                                                    track: [
                                                        "bg-danger-100",
                                                        fieldsLength >=
                                                            amountRecommendation.okay &&
                                                            "bg-warning-100",
                                                        fieldsLength >=
                                                            amountRecommendation.good &&
                                                            "bg-success-100",
                                                    ],
                                                }}
                                                aria-label="Item count"
                                                size="sm"
                                                value={
                                                    Math.min(
                                                        fieldsLength /
                                                            amountRecommendation.good,
                                                        1,
                                                    ) * 100
                                                }
                                                color={
                                                    fieldsLength >=
                                                    amountRecommendation.good
                                                        ? "success"
                                                        : fieldsLength >=
                                                            amountRecommendation.okay
                                                          ? "warning"
                                                          : "danger"
                                                }
                                            />
                                        </div>
                                    )}
                                </div>
                            )}
                            {(showEmptyLabel || isViewOnly) &&
                                (!fields || fields.length === 0) &&
                                (!staticItems || staticItems.length === 0) && (
                                    <div className="w-full text-center text-default-500 text-small">
                                        {emptyLabel}
                                    </div>
                                )}

                            {!hideItems &&
                                staticItems?.map((value, index) => (
                                    <BaseTextArrayFieldItem
                                        key={`static-${label} ${index + 1}`}
                                        id={`static-${label} ${index + 1}`}
                                        fieldName={fieldName}
                                        valuePropertyName={valuePropertyName}
                                        isDisabled={true}
                                        isViewOnly={isViewOnly}
                                        originalIndex={index}
                                        hideItemLabel={hideItemLabel}
                                        remove={remove}
                                        isAutofocusing={isAutofocusing}
                                        type={type}
                                        isRequired={isRequired}
                                        startContent={startContent}
                                        endContent={endContent}
                                        size={size}
                                        value={value}
                                        maxLength={maxLength}
                                        inputItem={inputItem}
                                        controlledInputItem={
                                            controlledInputItem
                                        }
                                    />
                                ))}

                            {fieldsWithIndexId.map((field, index) => (
                                <BaseTextArrayFieldItem
                                    key={field.id}
                                    id={field.id}
                                    formController={formController}
                                    fieldName={fieldName}
                                    isDisabled={isDisabled}
                                    originalIndex={index}
                                    label={`${label} ${index + 1}`}
                                    hideItemLabel={hideItemLabel}
                                    hideItem={hideItems}
                                    remove={remove}
                                    isAutofocusing={isAutofocusing}
                                    type={type}
                                    isRequired={isRequired}
                                    startContent={startContent}
                                    endContent={endContent}
                                    valuePropertyName={valuePropertyName}
                                    disableDnd={disableDnd}
                                    size={size}
                                    isViewOnly={isViewOnly}
                                    isInvalid={
                                        !!fieldError?.[index]?.[
                                            valuePropertyName
                                        ]?.message
                                    }
                                    errorMessage={
                                        fieldError?.[index]?.[valuePropertyName]
                                            ?.message
                                    }
                                    maxLength={maxLength}
                                    inputItem={inputItem}
                                    controlledInputItem={controlledInputItem}
                                />
                            ))}
                            <DragOverlay>
                                {activeItem ? (
                                    <BaseTextArrayFieldItem
                                        {...activeItem}
                                        valuePropertyName={valuePropertyName}
                                        originalIndex={Number.parseInt(
                                            activeItem.id.split("_")[1],
                                            10,
                                        )}
                                        formController={formController}
                                        fieldName={fieldName}
                                        remove={remove}
                                        maxLength={maxLength}
                                        inputItem={inputItem}
                                        controlledInputItem={
                                            controlledInputItem
                                        }
                                    />
                                ) : null}
                            </DragOverlay>
                            {!(hideAddButton || isViewOnly) && (
                                <Button
                                    size="md"
                                    variant="light"
                                    onPress={handleAppend}
                                    disableRipple={true}
                                    isDisabled={isDisabled || disableAddButton}
                                    startContent={
                                        <IconCirclePlus stroke={1.5} />
                                    }
                                >
                                    {`${label} hinzufügen`}
                                </Button>
                            )}
                            {isViewOnly &&
                                canCopyAll &&
                                !!fieldsWithIndexId.length && (
                                    <Button
                                        size="lg"
                                        variant="light"
                                        onPress={handleCopy}
                                        disableRipple={true}
                                        isDisabled={isDisabled}
                                        className="opacity-70"
                                        endContent={
                                            <div className="relative h-5 w-5">
                                                <IconCopy
                                                    stroke={1.5}
                                                    size={20}
                                                    className={cn(
                                                        "absolute transform text-default-500 transition delay-100 duration-200",
                                                        copied &&
                                                            "scale-0 delay-0",
                                                    )}
                                                />
                                                <IconCopyCheck
                                                    stroke={1.5}
                                                    size={20}
                                                    className={cn(
                                                        "absolute scale-0 transform text-success transition duration-200",
                                                        copied &&
                                                            "scale-100 delay-100",
                                                    )}
                                                />
                                            </div>
                                        }
                                    >
                                        Kopieren
                                    </Button>
                                )}
                            {!isViewOnly && cardEndContent?.(handleAppend)}
                        </CardBody>
                    </Card>
                    {/* We add an hidden input here so react hook from can scroll to it on error */}
                    <input name={fieldName} className="h-0 opacity-0" />
                    {fieldError &&
                        "message" in fieldError &&
                        fieldError.message && (
                            <div className="mt-1 text-danger text-tiny">
                                {fieldError.message as string}
                            </div>
                        )}
                </div>
            </SortableContext>
        </DndContext>
    );
};

const FormTextArrayField = <T extends FieldValues>({
    fieldName,
    rules,
    ai,
    formController,
    headerEndContent,
    ...otherProps
}: RegisteredTextArrayFieldProps<T>) => {
    const { fields, append, replace, move, remove, update } = useFieldArray({
        control: formController.control,
        name: fieldName,
        rules,
    });

    const fieldError = getValueAtPath(
        formController.formState.errors,
        fieldName,
    );

    const watchedValue = formController.watch(fieldName, []);

    const onAIResult = useCallback(
        (result) => {
            replace(
                result.map(
                    (value) =>
                        ({
                            [otherProps.valuePropertyName]: value.content,
                        }) as FieldArray<FieldValues, Path<T>>,
                ),
            );
        },
        [otherProps.valuePropertyName, replace],
    );

    const headerEndContentWithAI: RegisteredTextArrayFieldProps<T>["headerEndContent"] =
        (append) => (
            <>
                {headerEndContent?.(append)}
                {ai ? (
                    <div className="w-8">
                        <MagicWand
                            {...ai}
                            className="absolute top-2 right-2"
                            id={`${ai.uniqueId}_${fieldName}`}
                            inputValue={
                                watchedValue.map(
                                    (item) =>
                                        ({
                                            content:
                                                item[
                                                    otherProps.valuePropertyName
                                                ],
                                        }) satisfies TextArrayValue,
                                ) as TextArrayValue[]
                            }
                            onResult={onAIResult}
                        />
                    </div>
                ) : undefined}
            </>
        );

    return (
        <BaseTextArrayField
            append={append}
            update={update}
            move={move}
            remove={remove}
            fields={fields}
            formController={formController}
            fieldError={fieldError}
            rules={rules}
            fieldName={fieldName}
            {...otherProps}
            headerEndContent={headerEndContentWithAI}
        />
    );
};

const RegisteredTextArrayField = <T extends FieldValues>({
    // Ignore formController here, since it is passed via context
    formController: _,
    ...otherProps
}: RegisteredTextArrayFieldProps<T>) => {
    const formController = useFormControllerContext();

    return (
        <FormTextArrayField formController={formController} {...otherProps} />
    );
};

const TextArrayField = <
    T extends FieldValues,
    AIContextKeys extends string = string,
>({
    shouldRegister = true,
    ...otherProps
}: TextArrayFieldProps<T, AIContextKeys>) => {
    return shouldRegister ? (
        <RegisteredTextArrayField {...otherProps} />
    ) : (
        <BaseTextArrayField {...otherProps} />
    );
};

export { FormTextArrayField, TextArrayField };
